import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { apiRequest } from "@/lib/queryClient";

// Vue résultat d'un tournoi FFT (paires, rangs, points) — overlay au clic sur un tournoi de la fiche.
// ponytail: pas de club/tête de série/poids dans les données → omis (on a rang, noms, rangs FFT, points).

type Participant = { idCrm: string | null; prenom: string | null; nom: string | null; dernier_rang: number | null; partenaire_rang: number | null; partenaire: string | null; rang_obtenu: number | null; points: number | null };
type Data = { nom_tournoi?: string; niveau?: string; categorie?: string; expiration?: string; participants: Participant[] };

const fmtRank = (r: number | null | undefined) => (r == null ? "N/C" : "#" + Number(r).toLocaleString("fr-FR").replace(/ /g, " "));
const formatLabel = (c?: string) => ({ DM: "Hommes", DD: "Dames", DX: "Mixte", MX: "Mixte", SM: "Hommes", SD: "Dames" } as Record<string, string>)[c ?? ""] || c || "";

// Rang du joueur juste après son nom (même format jaune que les partenaires).
function NameRank({ name, rank }: { name: string; rank: number | null }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="font-bold text-pn-navy min-w-0 truncate">{name || "—"}</span>
      {rank != null && <span className="shrink-0 bg-pn-lime text-pn-navy text-xs font-bold px-2 py-0.5 rounded-full">{fmtRank(rank)}</span>}
    </div>
  );
}

export default function TournamentResultPN({ competitionId, onClose }: { competitionId: number; onClose: () => void }) {
  const { data, isLoading } = useQuery<Data>({
    queryKey: ["/api/competitions", competitionId, "participants"],
    queryFn: async () => (await apiRequest("GET", `/api/competitions/${competitionId}/participants`)).json(),
  });

  // Une paire par rang (chaque joueur produit une ligne → dédoublonnage par rang_obtenu).
  const pairs: Participant[] = [];
  const seen = new Set<number>();
  (data?.participants ?? []).forEach((p) => { if (p.rang_obtenu != null && !seen.has(p.rang_obtenu)) { seen.add(p.rang_obtenu); pairs.push(p); } });

  // TS (têtes de série) = classement des paires par poids (somme des 2 rangs), le plus faible = TS1.
  const poidsOf = (p: Participant) => p.dernier_rang != null && p.partenaire_rang != null ? Number(p.dernier_rang) + Number(p.partenaire_rang) : null;
  const ts = new Map<number, number>();
  pairs.filter((p) => poidsOf(p) != null).sort((a, b) => poidsOf(a)! - poidsOf(b)!).forEach((p, i) => ts.set(p.rang_obtenu!, i + 1));

  return (
    <div className="fixed inset-0 z-[80] bg-background overflow-y-auto font-body" role="dialog" aria-modal="true">
      <div className="flex items-center justify-between p-4 sticky top-0 bg-background z-10">
        <button onClick={onClose} aria-label="Fermer" className="w-11 h-11 rounded-full bg-muted border border-border flex items-center justify-center text-pn-navy"><X className="w-5 h-5" /></button>
        <h2 className="font-head font-extrabold text-xl text-pn-navy">Tournoi</h2>
        <span className="w-11" />
      </div>

      {isLoading || !data ? (
        <div className="p-6 space-y-4"><Skeleton className="h-24 w-full rounded-3xl" /><Skeleton className="h-28 w-full rounded-2xl" /></div>
      ) : (
        <div className="p-4 pb-10">
          <div className="rounded-3xl bg-pn-blue text-white p-5">
            <div className="font-head font-extrabold text-2xl">{data.niveau} {formatLabel(data.categorie)}</div>
            <div className="opacity-90 mt-1 font-semibold">{data.nom_tournoi}</div>
          </div>

          <div className="text-center my-4"><span className="bg-pn-lime text-pn-navy font-bold text-xs px-3 py-1.5 rounded-full">Résultats</span></div>

          <div className="space-y-3">
            {pairs.map((p) => {
              const poids = poidsOf(p);
              const seed = ts.get(p.rang_obtenu!);
              return (
                <div key={p.rang_obtenu!} className="rounded-2xl border border-border p-4">
                  <div className="flex items-center gap-4">
                    <div className="min-w-[48px] text-center font-head font-extrabold text-2xl text-pn-navy shrink-0">#{p.rang_obtenu}</div>
                    <div className="flex-1 min-w-0 space-y-2">
                      <NameRank name={[p.prenom, p.nom].filter(Boolean).join(" ")} rank={p.dernier_rang} />
                      <NameRank name={p.partenaire || ""} rank={p.partenaire_rang} />
                    </div>
                  </div>
                  <div className="mt-3 rounded-xl bg-muted/50 border border-border p-2.5 flex items-center justify-center gap-2 flex-wrap">
                    {seed != null && <span className="bg-pn-navy text-white font-bold text-sm rounded-full px-3 py-1">TS {seed}</span>}
                    {poids != null && <span className="bg-background border border-border text-pn-navy font-semibold text-sm rounded-full px-3 py-1">Poids : {fmtRank(poids)}</span>}
                    {p.points != null && <span className="bg-background border border-border text-pn-navy font-semibold text-sm rounded-full px-3 py-1">+{p.points} pts</span>}
                  </div>
                </div>
              );
            })}
            {pairs.length === 0 && <p className="text-center text-muted-foreground py-8">Pas de résultats disponibles.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
